import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, ClickAwayListener, Drawer, Fab, IconButton, TextField, Select, MenuItem, Typography, CircularProgress, Button,
} from '@mui/material';
import { n8nFetch } from '../../utils/n8n-fetch';
import { WEBHOOK_ENDPOINTS } from '../../config/webhook-urls';
import { getRole } from '../AuthWrapper';
import { Ico, TONE, mutedSx, emptySx, smallBtnSx, neutralBtnSx } from '../ui';
import { normalizePath } from '../../config/pageTitles';
import { getPageContext } from './page-context';

// Ассистент по данным фирм (пилот, только команда admin/manager) — .plan/assistant-1.md §2 C3.
// Монтируется в Layout сиблингом основного flex-Box: вкладки (TabsKeepAlive) не трогает.

const MODELS = [
  { value: 'anthropic/claude-haiku-5.5', label: 'Haiku 5.5 (по умолчанию)' },
  { value: 'openai/gpt-6-luna', label: 'GPT-6 Luna' },
  { value: 'anthropic/claude-sonnet-5.5', label: 'Sonnet 5.5' },
];
const TEAM_ROLES = ['admin', 'manager'];
const POLL_MS = 4000;
const POLL_LIMIT_MS = 240000;

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Строка «Страница: …» под вопросом — только ключи, что пришли с вопросом.
const contextLine = (x) => {
  if (!x || !x.path) return '';
  const parts = [x.path];
  if (x.nmId != null) parts.push(`nmId ${x.nmId}`);
  if (x.sku != null) parts.push(`sku ${x.sku}`);
  if (x.offer_id) parts.push(`offer_id ${x.offer_id}`);
  if (Array.isArray(x.nmIds) && x.nmIds.length > 1) parts.push(`выбрано ${x.nmIds.length}`);
  if (x.seller_name) parts.push(`фирма ${x.seller_name}`);
  else if (x.user_id != null) parts.push(`user_id ${x.user_id}`);
  if (x.date_start || x.date_end) parts.push(`${x.date_start || ''}…${x.date_end || ''}`);
  else if (x.days) parts.push(`${x.days} дн.`);
  return `Страница: ${parts.join(' · ')}`;
};

// POST /webhook/assistant; разбор ответа — как в utils/comm-api.js (конверт n8n [{json}] / {json}).
async function assistantApi(payload) {
  const res = await n8nFetch(WEBHOOK_ENDPOINTS.ASSISTANT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* пустое тело — ниже ошибка по статусу */
  }
  if (Array.isArray(data) && data[0] && typeof data[0] === 'object' && data[0].json !== undefined) data = data[0].json;
  else if (data && typeof data === 'object' && data.json !== undefined) data = data.json;
  if (!res.ok) {
    throw new Error((data && data.error) || (res.status === 403 ? 'Ассистент доступен только команде' : `HTTP ${res.status}`));
  }
  if (!data || data.ok === false) throw new Error((data && data.error) || `HTTP ${res.status}: пустой ответ сервера`);
  return data;
}

// Markdown ответа модели: сырой HTML react-markdown не рендерит (rehype-raw не подключать).
const MD_COMPONENTS = {
  a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
};
const mdSx = {
  '& p': { margin: '0 0 6px' },
  '& > :last-child': { marginBottom: 0 },
  '& ul, & ol': { margin: '4px 0', paddingLeft: '18px' },
  '& table': {
    borderCollapse: 'collapse', fontSize: 11.5, margin: '6px 0',
    display: 'block', maxWidth: '100%', overflowX: 'auto', whiteSpace: 'nowrap',
  },
  '& th, & td': { border: '1px solid var(--color-neutral-300)', padding: '2px 6px', textAlign: 'left' },
  '& th': { background: 'var(--color-neutral-100)', fontWeight: 600 },
  '& code': { fontSize: 11.5, background: 'var(--color-neutral-100)', padding: '0 3px', borderRadius: '3px' },
  '& pre': { overflowX: 'auto', fontSize: 11.5, margin: '6px 0' },
  '& h1, & h2, & h3': { fontSize: 13, margin: '8px 0 4px' },
};

// react-markdown/remark-gfm — только в браузере: их vfile импортирует `node:path`/`node:process`/`node:url`,
// а валидатор SSR-движков Gatsby 5.15 `node:`-импорты не пускает (ENGINE.VALIDATION 98001) — статический импорт роняет build.
let mdModule = null;
let mdPromise = null;
const loadMarkdown = () => {
  if (!mdPromise) {
    mdPromise = Promise.all([import('react-markdown'), import('remark-gfm')])
      .then(([rm, gfm]) => { mdModule = { ReactMarkdown: rm.default, remarkGfm: gfm.default }; return mdModule; })
      .catch((e) => { mdPromise = null; throw e; });
  }
  return mdPromise;
};

function Markdown({ text }) {
  const [md, setMd] = useState(mdModule);
  useEffect(() => {
    if (md) return undefined;
    let alive = true;
    loadMarkdown().then((m) => { if (alive) setMd(m); }).catch(() => { /* остаётся простой текст */ });
    return () => { alive = false; };
  }, [md]);
  if (!md) return <Box sx={{ whiteSpace: 'pre-wrap' }}>{text}</Box>;
  const { ReactMarkdown, remarkGfm } = md;
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{text}</ReactMarkdown>;
}

function Bubble({ msg }) {
  const [showSources, setShowSources] = useState(false);
  const isUser = msg.role === 'user';
  const isError = msg.role === 'error';
  const c = msg.content || {};
  const u = msg.usage;
  const sources = Array.isArray(c.sources) ? c.sources.filter(Boolean) : [];
  const ctx = isUser ? contextLine(c.context) : '';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.25 }}>
      <Box sx={{ maxWidth: isUser ? '85%' : '92%', minWidth: 0 }}>
        <Box
          sx={{
            px: 1.5, py: 1, fontSize: 12.5, lineHeight: 1.45, wordBreak: 'break-word',
            ...(isUser ? { whiteSpace: 'pre-wrap' } : mdSx),
            borderRadius: isUser ? '10px 10px 3px 10px' : '10px 10px 10px 3px',
            bgcolor: isUser ? 'var(--color-accent-200)' : isError ? TONE.err.bg : 'var(--color-neutral-200)',
            color: isUser ? 'var(--color-accent-900)' : isError ? TONE.err.fg : 'var(--color-neutral-900)',
          }}
        >
          {isUser ? c.text : (
            <Markdown text={String(c.answer ?? '')} />
          )}
        </Box>
        {ctx && <Typography sx={{ ...mutedSx, mt: 0.5, textAlign: 'right' }}>{ctx}</Typography>}
        {msg.role === 'assistant' && sources.length > 0 && (
          <>
            <Typography
              component="button"
              type="button"
              onClick={() => setShowSources((v) => !v)}
              sx={{ ...mutedSx, mt: 0.5, p: 0, border: 0, background: 'none', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {`Запросы (${sources.length}) ${showSources ? '▴' : '▾'}`}
            </Typography>
            {showSources && sources.map((s, i) => (
              <Typography key={i} sx={{ ...mutedSx, fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                {String(s)}
              </Typography>
            ))}
          </>
        )}
        {msg.role === 'assistant' && u && (
          <Typography sx={mutedSx}>
            {`${u.model || c.model || ''} · in ${u.prompt_tokens ?? 0} / out ${u.completion_tokens ?? 0} токенов`}
            {typeof u.cost_usd === 'number' ? ` · $${u.cost_usd.toFixed(4)}` : ''}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function AssistantDrawer() {
  const [role, setRole] = useState(null);
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState(MODELS[0].value);
  const [view, setView] = useState('list'); // 'list' | 'thread'
  const [chats, setChats] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const pollRef = useRef({ timer: null, token: 0 });
  const listRef = useRef(null);

  useEffect(() => {
    const sync = () => {
      const r = getRole();
      setRole(r);
      if (!TEAM_ROLES.includes(r)) {
        // разлогин / смена роли: гасим поллинг и панель
        clearTimeout(pollRef.current.timer);
        pollRef.current.token += 1;
        setOpen(false);
      }
    };
    sync();
    window.addEventListener('auth-changed', sync);
    return () => window.removeEventListener('auth-changed', sync);
  }, []);

  // Останавливает поллинг; token отсекает ответы запросов, ушедших до остановки.
  const stopPoll = useCallback(() => {
    clearTimeout(pollRef.current.timer);
    pollRef.current.timer = null;
    pollRef.current.token += 1;
    setPending(false);
  }, []);

  useEffect(() => () => {
    clearTimeout(pollRef.current.timer);
    pollRef.current.token += 1;
  }, []);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await assistantApi({ action: 'list' });
      setChats(Array.isArray(data.chats) ? data.chats : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, pending]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !e.defaultPrevented) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const openChat = async (id) => {
    stopPoll();
    setError('');
    setChatId(id);
    setMessages([]);
    setView('thread');
    setThreadLoading(true);
    const token = pollRef.current.token;
    try {
      const data = await assistantApi({ action: 'history', chat_id: id });
      if (pollRef.current.token === token) setMessages(Array.isArray(data.messages) ? data.messages : []);
    } catch (e) {
      if (pollRef.current.token === token) setError(e.message);
    } finally {
      if (pollRef.current.token === token) setThreadLoading(false);
    }
  };

  const newChat = () => {
    stopPoll();
    setError('');
    setChatId(null);
    setMessages([]);
    setText('');
    setThreadLoading(false);
    setView('thread');
  };

  const backToList = () => {
    stopPoll();
    setError('');
    setView('list');
    loadList();
  };

  const poll = (id, messageId, startedAt, token) => {
    pollRef.current.timer = setTimeout(async () => {
      let list = null;
      try {
        const data = await assistantApi({ action: 'history', chat_id: id });
        list = Array.isArray(data.messages) ? data.messages : [];
      } catch {
        /* сетевой сбой — пробуем снова до общего лимита */
      }
      if (pollRef.current.token !== token) return;
      const done = list && list.some((m) => Number(m.id) > Number(messageId) && (m.role === 'assistant' || m.role === 'error'));
      if (done) {
        setMessages(list);
        setPending(false);
      } else if (Date.now() - startedAt >= POLL_LIMIT_MS) {
        setMessages((prev) => [...prev, {
          id: `local-timeout-${Date.now()}`,
          role: 'error',
          content: { answer: 'Ответ не пришёл за 4 минуты — откройте диалог позже' },
        }]);
        setPending(false);
      } else {
        poll(id, messageId, startedAt, token);
      }
    }, POLL_MS);
  };

  const send = async () => {
    const question = text.trim();
    if (!question || pending) return;
    stopPoll();
    setError('');
    setPending(true);
    const token = pollRef.current.token;
    // Активная вкладка keep-alive = текущий pathname (как activePath в useSiteTabs).
    const path = normalizePath(window.location.pathname);
    const context = { path, ...(getPageContext(path) || {}) };
    try {
      const data = await assistantApi({ action: 'ask', chat_id: chatId || null, question, model, context });
      if (pollRef.current.token !== token) return;
      const isNew = !chatId;
      setChatId(data.chat_id);
      setText('');
      setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: 'user', content: { text: question, context } }]);
      if (isNew) loadList();
      poll(data.chat_id, data.message_id, Date.now(), token);
    } catch (e) {
      if (pollRef.current.token !== token) return;
      setError(e.message);
      setPending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!TEAM_ROLES.includes(role)) return null;

  return (
    <>
      {!open && (
        <Fab
          size="small"
          color="primary"
          aria-label="Ассистент"
          data-assistant-fab
          onClick={() => setOpen(true)}
          sx={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1250, '@media print': { display: 'none' } }}
        >
          <Ico name="ph-sparkle" size={20} />
        </Fab>
      )}
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={(e) => {
          if (e.target instanceof Element && e.target.closest('[data-assistant-fab]')) return;
          if (open) setOpen(false);
        }}
      >
      <Drawer
        anchor="right"
        variant="persistent"
        open={open}
        PaperProps={{
          sx: {
            width: 440, maxWidth: '100vw', display: 'flex', flexDirection: 'column',
            borderLeft: '1px solid var(--color-neutral-300)', background: 'var(--surface-card)',
            '@media print': { display: 'none' },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: '1px solid var(--color-neutral-200)', flexShrink: 0 }}>
          <Ico name="ph-sparkle" size={14} color="var(--color-accent-700)" />
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, mr: 'auto' }}>Ассистент</Typography>
          <Select size="small" value={model} onChange={(e) => setModel(e.target.value)} sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}>
            {MODELS.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 12.5 }}>{m.label}</MenuItem>)}
          </Select>
          <Button size="small" variant="outlined" sx={{ ...neutralBtnSx, whiteSpace: 'nowrap', flexShrink: 0 }} onClick={newChat}>Новый диалог</Button>
          <IconButton size="small" aria-label="Закрыть" onClick={() => setOpen(false)}>
            <Ico name="ph-x" size={16} />
          </IconButton>
        </Box>

        {view === 'list' ? (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {error && <Typography sx={{ fontSize: 11.5, color: TONE.err.fg, px: 2, pt: 1 }}>{error}</Typography>}
            {listLoading && chats.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
            ) : chats.length === 0 ? (
              <Typography sx={emptySx}>Диалогов пока нет — начните новый</Typography>
            ) : (
              chats.map((c) => (
                <Box
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openChat(c.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openChat(c.id); }}
                  sx={{ px: 2, py: 1, cursor: 'pointer', borderBottom: '1px solid var(--color-neutral-200)', '&:hover': { bgcolor: 'var(--color-neutral-100)' } }}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.title || 'Без названия'}
                  </Typography>
                  <Typography sx={mutedSx}>{[c.model, fmtDate(c.created_at)].filter(Boolean).join(' · ')}</Typography>
                </Box>
              ))
            )}
          </Box>
        ) : (
          <>
            <Box sx={{ px: 1, py: 0.5, borderBottom: '1px solid var(--color-neutral-200)', flexShrink: 0 }}>
              <Button size="small" sx={{ ...smallBtnSx, color: 'var(--color-neutral-700)' }} onClick={backToList}>← Диалоги</Button>
            </Box>
            <Box ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
              {threadLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
              ) : messages.length === 0 && !pending ? (
                <Typography sx={emptySx}>Задайте вопрос по данным фирм</Typography>
              ) : (
                messages.map((m, i) => <Bubble key={m.id ?? i} msg={m} />)
              )}
              {pending && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...mutedSx }}>
                  <CircularProgress size={14} /> Думаю…
                </Box>
              )}
            </Box>
            {error && <Typography sx={{ fontSize: 11.5, color: TONE.err.fg, px: 2, pt: 1, flexShrink: 0 }}>{error}</Typography>}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', px: 2, py: 1.5, flexShrink: 0, borderTop: '1px solid var(--color-neutral-200)' }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder="Вопрос по данным…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={pending}
              />
              <IconButton
                aria-label="Отправить"
                onClick={send}
                disabled={!text.trim() || pending}
                sx={{ color: 'var(--color-accent-700)', border: '1px solid var(--color-accent-600)', borderRadius: 'var(--radius-md)', '&.Mui-disabled': { borderColor: 'var(--color-neutral-300)' } }}
              >
                {pending ? <CircularProgress size={18} /> : <Ico name="ph-paper-plane-tilt" size={18} />}
              </IconButton>
            </Box>
          </>
        )}
      </Drawer>
      </ClickAwayListener>
    </>
  );
}
